import { ReportTypeEnum } from '@revelio/data-access';

import { REPORT_CAPABILITY_MODELS } from './catalog-capabilities';
import type { GlobalAssistantReportCatalogEntry } from './types';

/**
 * Global AI assistant report catalog.
 *
 * Parameter semantics:
 * - isPrimary: true — anchor slot the report is built around (e.g. target geography, focus company).
 * - isPrimary: false with current/target naming — comparison side of a paired analysis.
 * - isPrimary: false with supportsMultiple — optional overflow filters (extra roles, skills, geographies).
 */

const GEOGRAPHY_CATEGORIES = [
  'region',
  'country',
  'metro_area',
  'msa',
  'state',
  'city',
] as const;

const ROLE_CATEGORIES = [
  'role_k50',
  'role_k150',
  'role_k500',
  'role_k1500',
] as const;

const COMPANY_CATEGORIES = ['company'] as const;

const COUNTRY_CATEGORIES = ['country'] as const;

const SKILL_CATEGORIES = [
  'skill_k25',
  'skill_k50',
  'skill_k75',
  'skill_k150',
  'skill_k700',
  'skill_k1500',
  'skill_k2500',
  'skill_k5000',
  'skill_0',
  'skill_1',
  'skill_2',
  'skill_3',
] as const;

const SENIORITY_CATEGORIES = ['seniority'] as const;

export const GLOBAL_AI_ASSISTANT_REPORT_SELECTOR_CONFIDENCE_THRESHOLD = 70;

export const GLOBAL_ASSISTANT_REPORT_CATALOG: readonly GlobalAssistantReportCatalogEntry[] =
  [
    {
      reportType: ReportTypeEnum.JobComparison,
      enabled: true,
      reportName: 'Job Comparison',
      description: '15-page comparison of two positions',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_job_comparison',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User compares two jobs, roles, offers, companies, metros, or seniority levels for a candidate or recruiter.',
        whenNotToPick:
          'General market conditions (prefer Market Conditions) or sentiment-only questions.',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.JobComparison],
      parameters: [
        {
          parameterId: 'current-role',
          displayName: 'Current Role',
          mwColumnLabels: ['Current Role'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: true,
          resolverKind: 'role',
        },
        {
          parameterId: 'target-role',
          displayName: 'Target Role',
          mwColumnLabels: ['Target Role'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: false,
          resolverKind: 'role',
        },
        {
          parameterId: 'current-metro',
          displayName: 'Current Metro Area',
          mwColumnLabels: ['Current Metro Area'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'target-metro',
          displayName: 'Target Metro Area',
          mwColumnLabels: ['Target Metro Area'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'current-company',
          displayName: 'Current Company',
          mwColumnLabels: ['Current Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'target-company',
          displayName: 'Target Company',
          mwColumnLabels: ['Target Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'company',
        },
        {
          parameterId: 'current-seniority',
          displayName: 'Current Seniority',
          mwColumnLabels: ['Current Seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'list',
          listFilterType: 'seniority',
        },
        {
          parameterId: 'target-seniority',
          displayName: 'Target Seniority',
          mwColumnLabels: ['Target Seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'list',
          listFilterType: 'seniority',
        },
      ],
      projectionRules: [
        {
          kind: 'currentTargetPair',
          field: 'role',
          currentParameterId: 'current-role',
          targetParameterId: 'target-role',
        },
        {
          kind: 'currentTargetPair',
          field: 'geography',
          currentParameterId: 'current-metro',
          targetParameterId: 'target-metro',
        },
        {
          kind: 'currentTargetPair',
          field: 'company',
          currentParameterId: 'current-company',
          targetParameterId: 'target-company',
        },
        {
          kind: 'currentTargetPair',
          field: 'seniority',
          currentParameterId: 'current-seniority',
          targetParameterId: 'target-seniority',
        },
      ],
    },
    {
      reportType: ReportTypeEnum.MarketTightness,
      enabled: true,
      reportName: 'Market Conditions',
      description: '14-page overview of key factors to compare markets',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_market_tightness',
      launchTarget: 'promoted',
      templateId: 'report-company-market-conditions',
      selectorHints: {
        whenToPick:
          'User asks about labor market conditions, hiring difficulty, market tightness, or comparing geographies/roles for hiring.',
        whenNotToPick:
          'Job-to-job comparison for one candidate (prefer Job Comparison) or employee sentiment (prefer Sentiment).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.MarketTightness],
      parameters: [
        {
          parameterId: 'target-geography',
          displayName: 'Target Geography',
          mwColumnLabels: ['Focus Geography', 'Target Geography'],
          promotedParameterIds: ['target-geography'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
        },
        {
          parameterId: 'comparison-geography',
          displayName: 'Comparison Geography',
          mwColumnLabels: [
            'Geography Comparison Group',
            'Comparison Geography',
          ],
          promotedParameterIds: ['comparison-geography'],
          expectedSelectionCategories: GEOGRAPHY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'geography',
          geographyMaxLevel: 'msa',
          supportsMultiple: true,
        },
        {
          parameterId: 'occupation',
          displayName: 'Occupation',
          mwColumnLabels: ['Occupation'],
          promotedParameterIds: ['occupation'],
          expectedSelectionCategories: ROLE_CATEGORIES,
          isPrimary: false,
          resolverKind: 'role',
          supportsMultiple: true,
        },
        {
          parameterId: 'skills',
          displayName: 'Skills',
          mwColumnLabels: ['Skills'],
          promotedParameterIds: ['skills'],
          expectedSelectionCategories: SKILL_CATEGORIES,
          isPrimary: false,
          resolverKind: 'skill',
          supportsMultiple: true,
        },
        {
          parameterId: 'seniority',
          displayName: 'Seniority',
          mwColumnLabels: ['Seniority'],
          promotedParameterIds: ['seniority'],
          expectedSelectionCategories: SENIORITY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'list',
          listFilterType: 'seniority',
          supportsMultiple: true,
        },
      ],
      projectionRules: [
        {
          kind: 'geographyPromptOrdered',
          primaryParameterId: 'target-geography',
          restParameterId: 'comparison-geography',
        },
        { kind: 'tdFieldAll', field: 'role', parameterId: 'occupation' },
        { kind: 'tdFieldAll', field: 'skill', parameterId: 'skills' },
        { kind: 'tdFieldAll', field: 'seniority', parameterId: 'seniority' },
      ],
    },
    {
      reportType: ReportTypeEnum.Sentiment,
      enabled: true,
      reportName: 'Sentiment',
      description: 'Company-level employee sentiment analysis',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_sentiment',
      launchTarget: 'promoted',
      templateId: 'report-company-sentiment',
      selectorHints: {
        whenToPick:
          'User asks about employee sentiment, culture, reviews, why people leave or stay at a company, or competitor sentiment comparisons.',
        whenNotToPick:
          'Prestige or education background analysis (prefer Prestige) or labor market tightness (prefer Market Conditions).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.Sentiment],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          promotedParameterIds: ['622c111e-8d5d-426a-9c51-c4053a2b4c8d'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'competitors',
          displayName: 'Competitors',
          mwColumnLabels: ['Comparison Company Group', 'Competitors'],
          promotedParameterIds: ['99a1ccb1-1940-4904-863a-21acb7aa2fb8'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'company',
          supportsMultiple: true,
        },
      ],
      projectionRules: [
        {
          kind: 'companyPrimaryRest',
          primaryParameterId: 'company',
          restParameterId: 'competitors',
        },
      ],
    },
    {
      reportType: ReportTypeEnum.Prestige,
      enabled: true,
      reportName: 'Prestige',
      description:
        '19-page analysis of employee education and corporate backgrounds',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_prestige',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User asks about company prestige scores, employer reputation rankings, or prestige benchmarks for a specific company (e.g. "prestige score for Amazon").',
        whenNotToPick:
          'General sentiment or culture questions (prefer Sentiment).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.Prestige],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.AiAdoptionExposure,
      enabled: true,
      reportName: 'AI Exposure and Adoption',
      description: '12-page analysis of AI exposure and adoption',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_ai_adoption_exposure',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User asks about AI exposure, AI adoption, automation risk, or AI readiness for a named company versus peers.',
        whenNotToPick:
          'Employee sentiment or culture (prefer Sentiment), prestige or reputation (prefer Prestige), or labor market tightness (prefer Market Conditions).',
      },
      capabilityModel:
        REPORT_CAPABILITY_MODELS[ReportTypeEnum.AiAdoptionExposure],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.Compensation,
      enabled: true,
      reportName: 'Compensation Summary',
      description: '16-page analysis of organizational compensation strategy',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_compensation',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's compensation strategy, salary or total-compensation benchmarking vs peers/competitors, pay by geography or role, salary satisfaction, or talent sources by pay.",
        whenNotToPick:
          'Two-offer job comparison for one candidate (prefer Job Comparison), labor market tightness or hiring difficulty (prefer Market Conditions), employee sentiment or culture (prefer Sentiment), or prestige/reputation (prefer Prestige).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.Compensation],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.Attrition,
      enabled: true,
      reportName: 'Attrition',
      description:
        "9-page analysis of a company's attrition vs top outflow destinations",
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_attrition',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's attrition, turnover, or retention vs peers/outflow destinations, where former employees are going, or attrition by role or geography.",
        whenNotToPick:
          'Two-offer job comparison for one candidate (prefer Job Comparison), labor market tightness or hiring difficulty (prefer Market Conditions), compensation benchmarking without a retention focus (prefer Compensation Summary), employee sentiment or culture without attrition framing (prefer Sentiment), or prestige/reputation (prefer Prestige).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.Attrition],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.GenderDiversity,
      enabled: true,
      reportName: 'Gender Diversity',
      description:
        '20-page analysis of organizational gender composition vs competitors',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_gender_diversity',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's gender diversity, gender composition or balance, share of women, gender pay equity, or female representation by role, seniority, or geography vs peers.",
        whenNotToPick:
          'Employee sentiment or culture without a gender focus (prefer Sentiment), attrition or turnover without a gender focus (prefer Attrition), compensation benchmarking without a gender-equity focus (prefer Compensation Summary), labor market tightness (prefer Market Conditions), two-offer job comparison for one candidate (prefer Job Comparison), or prestige/reputation (prefer Prestige).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.GenderDiversity],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.HcDisclosure,
      enabled: true,
      reportName: 'Human Capital Disclosure',
      description:
        "10-page report of a company's headcount and employee breakdown",
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_hc_disclosure',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's own headcount, human capital metrics, contingent worker share, total payroll, or employee composition by geography, role, gender, or ethnicity for disclosure/reporting (not vs peers).",
        whenNotToPick:
          'Benchmarking gender composition or pay equity vs peers (prefer Gender Diversity), salary/compensation benchmarking vs peers (prefer Compensation Summary), attrition or turnover (prefer Attrition), employee sentiment or culture (prefer Sentiment), labor market tightness (prefer Market Conditions), prestige/reputation (prefer Prestige), or two-offer job comparison (prefer Job Comparison).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.HcDisclosure],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.Skills,
      enabled: true,
      reportName: 'Skills',
      description: '12-page overview of employee skills with recommendations',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_skills',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's workforce skills or competencies vs peers, skill prevalence, growing or declining skills, skill supply and demand gaps, salary premium by skill, or build-vs-buy / upskilling / L&D / internal mobility opportunities.",
        whenNotToPick:
          'Salary or compensation benchmarking without a skills focus (prefer Compensation Summary), labor market tightness or hiring difficulty by geography (prefer Market Conditions), AI exposure or adoption (prefer AI Exposure and Adoption), employee sentiment or culture (prefer Sentiment), prestige/reputation (prefer Prestige), attrition or turnover (prefer Attrition), gender diversity (prefer Gender Diversity), or two-offer job comparison (prefer Job Comparison).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.Skills],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.SalesCompensation,
      enabled: true,
      reportName: 'Sales Compensation',
      description: '17-page analysis of sales employee compensation',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_sales_compensation',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          "User asks about a named company's sales-force compensation vs competitors: sales roles (account executive, account manager, SDR/BDR/LDR), base pay vs on-target earnings or variable/commission, sales comp by role, division, or metro, sales salary satisfaction, or highest potential earnings.",
        whenNotToPick:
          'Org-wide or non-sales compensation and salary benchmarking (prefer Compensation Summary), labor market tightness or hiring difficulty (prefer Market Conditions), workforce skills or competencies (prefer Skills), employee sentiment or culture (prefer Sentiment), attrition or turnover without a sales-pay focus (prefer Attrition), prestige/reputation (prefer Prestige), or two-offer job comparison for one candidate (prefer Job Comparison).',
      },
      capabilityModel:
        REPORT_CAPABILITY_MODELS[ReportTypeEnum.SalesCompensation],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'competitors',
          displayName: 'Comparison Company Group',
          mwColumnLabels: ['Comparison Company Group', 'Competitors'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'company',
          supportsMultiple: true,
        },
      ],
      projectionRules: [
        {
          kind: 'companyPrimaryRest',
          primaryParameterId: 'company',
          restParameterId: 'competitors',
        },
      ],
    },
    {
      reportType: ReportTypeEnum.OrgStructure,
      enabled: true,
      reportName: 'Organizational Structure',
      description: 'Interactive organizational chart of employees at a company',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_org_structure',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User asks for a named company\'s org chart, organizational structure, reporting lines, leadership hierarchy, or who reports to whom (e.g. "org chart for Pfizer").',
        whenNotToPick:
          'Headcount composition or human-capital disclosure metrics (prefer Human Capital Disclosure), gender composition (prefer Gender Diversity), compensation (prefer Compensation Summary), employee sentiment or culture (prefer Sentiment), or prestige/reputation (prefer Prestige).',
      },
      capabilityModel: REPORT_CAPABILITY_MODELS[ReportTypeEnum.OrgStructure],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
      ],
      projectionRules: [{ kind: 'companyPrimary', parameterId: 'company' }],
    },
    {
      reportType: ReportTypeEnum.CompensationBenchmarking,
      enabled: true,
      reportName: 'Compensation Benchmarking',
      description: 'In-depth analysis of role compensation vs industry peers',
      sampleLink:
        'https://deliverables.reveliolabs.com/report/?id=sample_compensation_benchmark',
      launchTarget: 'middleware',
      selectorHints: {
        whenToPick:
          'User asks how a named company pays a specific role or occupation in a specific country compared to industry peers or competitors (e.g. how Amazon pays software engineers in the US vs peers).',
        whenNotToPick:
          'Org-wide compensation strategy or salary benchmarking without a specific role and country (prefer Compensation Summary), labor market tightness or hiring difficulty (prefer Market Conditions), or two-offer job comparison for one candidate (prefer Job Comparison).',
      },
      capabilityModel:
        REPORT_CAPABILITY_MODELS[ReportTypeEnum.CompensationBenchmarking],
      parameters: [
        {
          parameterId: 'company',
          displayName: 'Company',
          mwColumnLabels: ['Company'],
          expectedSelectionCategories: COMPANY_CATEGORIES,
          isPrimary: true,
          resolverKind: 'company',
        },
        {
          parameterId: 'occupation',
          displayName: 'Occupation',
          mwColumnLabels: ['Occupation'],
          expectedSelectionCategories: ['role_k150'],
          isPrimary: false,
          resolverKind: 'role',
        },
        {
          parameterId: 'country',
          displayName: 'Country',
          mwColumnLabels: ['Country'],
          expectedSelectionCategories: COUNTRY_CATEGORIES,
          isPrimary: false,
          resolverKind: 'geography',
        },
      ],
      projectionRules: [
        { kind: 'companyPrimary', parameterId: 'company' },
        { kind: 'tdFieldAll', field: 'role', parameterId: 'occupation' },
        { kind: 'tdFieldAll', field: 'geography', parameterId: 'country' },
      ],
    },
  ];
